import type { CurrencyFormat, MathConfig, ResolvedTheme, Strings } from '../types';
import { formatMoney, t } from '../util/format';
import { createModal, el, type Modal } from './modal';

/** Paytable / info overlay. Pays are shown in money for the current bet. */
export class Paytable {
  private readonly root: HTMLElement;
  private readonly math: MathConfig;
  private theme: ResolvedTheme;
  private strings: Strings;
  private readonly currency: CurrencyFormat;
  private modal: Modal | null = null;

  constructor(o: { root: HTMLElement; math: MathConfig; theme: ResolvedTheme; strings: Strings; currency: CurrencyFormat }) {
    this.root = o.root;
    this.math = o.math;
    this.theme = o.theme;
    this.strings = o.strings;
    this.currency = o.currency;
  }

  get isOpen(): boolean {
    return !!this.modal?.open;
  }

  setTheme(theme: ResolvedTheme): void {
    this.theme = theme;
  }

  setStrings(strings: Strings): void {
    this.strings = strings;
  }

  private symbolName(id: string): string {
    return this.strings[`symbol.${id}`] ?? this.math.symbols.find((s) => s.id === id)?.name ?? id;
  }

  open(bet: number, onClose?: () => void): void {
    this.close();
    const S = this.strings;
    const m = this.math;
    const lineBet = bet / m.paylines.length;
    this.modal = createModal(this.root, { title: t(S, 'paytable'), size: 'wide', closeLabel: t(S, 'close'), onClose });
    const body = this.modal.body;

    // Summary chips
    const chips = el('div', 'slot-paytable__chips');
    const chip = (label: string, value: string) => {
      const c = el('span', 'slot-chip');
      c.append(document.createTextNode(`${label} `), el('b', undefined, value));
      chips.appendChild(c);
    };
    chip(t(S, 'rtp'), m.rtpLabel);
    chip(t(S, 'volatility'), t(S, `volatility.${m.volatility}`));
    chip(t(S, 'maxWin'), `${m.maxWinMultiplier}x`);
    chip(t(S, 'lines'), String(m.paylines.length));
    chip(t(S, 'bet'), `${formatMoney(m.bets[0], this.currency)} – ${formatMoney(m.bets[m.bets.length - 1], this.currency)}`);
    body.appendChild(chips);

    // Paying symbols
    const grid = el('div', 'slot-symbols');
    const order = ['premium', 'high', 'mid', 'low'];
    const paying = m.symbols.filter((s) => s.pays && !s.scatter && !s.wild).sort((a, b) => order.indexOf(a.tier) - order.indexOf(b.tier));
    for (const s of paying) {
      const card = el('div', 'slot-symbol');
      const img = el('img', 'slot-symbol__img');
      img.src = this.theme.assets.symbols[s.id]?.image ?? '';
      img.alt = this.symbolName(s.id);
      const bodyEl = el('div', 'slot-symbol__body');
      bodyEl.appendChild(el('div', 'slot-symbol__name', this.symbolName(s.id)));
      const pays = el('div', 'slot-symbol__pays');
      const counts = Object.keys(s.pays!).map(Number).sort((a, b) => b - a);
      for (const n of counts) {
        pays.append(el('span', undefined, `${n}×`), el('span', undefined, formatMoney(s.pays![n] * lineBet, this.currency)));
      }
      bodyEl.appendChild(pays);
      card.append(img, bodyEl);
      grid.appendChild(card);
    }
    body.appendChild(grid);

    // Special symbols
    const specials = el('div', 'slot-symbols');
    const wild = m.symbols.find((s) => s.wild);
    const scatter = m.scatter ? m.symbols.find((s) => s.id === m.scatter!.symbol) : undefined;
    if (wild) specials.appendChild(this.specialCard(wild.id, t(S, 'wildRule')));
    if (scatter && m.scatter) {
      const sc = m.scatter;
      const counts = Object.keys(sc.pays).map(Number).sort((a, b) => b - a);
      const card = this.specialCard(scatter.id, t(S, 'scatterRule'));
      const pays = el('div', 'slot-symbol__pays');
      for (const n of counts) pays.append(el('span', undefined, `${n}×`), el('span', undefined, formatMoney(sc.pays[n] * bet, this.currency)));
      card.querySelector('.slot-symbol__body')?.appendChild(pays);
      specials.appendChild(card);
    }
    body.appendChild(specials);

    // Rules
    body.appendChild(el('h3', 'slot-paytable__section', t(S, 'rules')));
    const rules = el('ul', 'slot-rules');
    rules.appendChild(el('li', undefined, t(S, 'linesRule', { lines: m.paylines.length })));
    if (wild) rules.appendChild(el('li', undefined, t(S, 'wildRule')));
    if (m.scatter) {
      const fs = m.scatter.freeSpins;
      const fsCounts = Object.keys(fs).map(Number).sort((a, b) => a - b);
      rules.appendChild(el('li', undefined, t(S, 'scatterRule')));
      rules.appendChild(
        el('li', undefined, t(S, 'freeSpinsRule', { a: fs[fsCounts[0]] ?? '', b: fs[fsCounts[1]] ?? '', c: fs[fsCounts[2]] ?? '', m: m.freeSpins.multiplier })),
      );
    }
    rules.appendChild(el('li', undefined, t(S, 'maxWinRule', { x: m.maxWinMultiplier })));
    body.appendChild(rules);

    // Paylines
    body.appendChild(el('h3', 'slot-paytable__section', t(S, 'paylinesTitle')));
    const lines = el('div', 'slot-paylines');
    m.paylines.forEach((rows, i) => {
      const item = el('div', 'slot-payline');
      const g = el('div', 'slot-payline__grid');
      g.style.gridTemplateColumns = `repeat(${m.reels}, 1fr)`;
      const color = this.theme.palette.paylines[i % this.theme.palette.paylines.length];
      for (let row = 0; row < m.rows; row++) {
        for (let reel = 0; reel < m.reels; reel++) {
          const cell = el('div', 'slot-payline__cell');
          if (rows[reel] === row) {
            cell.classList.add('is-on');
            cell.style.background = color;
          }
          g.appendChild(cell);
        }
      }
      item.append(g, el('span', undefined, String(i + 1)));
      lines.appendChild(item);
    });
    body.appendChild(lines);
  }

  private specialCard(id: string, rule: string): HTMLElement {
    const card = el('div', 'slot-symbol slot-symbol--special');
    const img = el('img', 'slot-symbol__img');
    img.src = this.theme.assets.symbols[id]?.image ?? '';
    img.alt = this.symbolName(id);
    const bodyEl = el('div', 'slot-symbol__body');
    bodyEl.append(el('div', 'slot-symbol__name', this.symbolName(id)), el('p', 'slot-symbol__rule', rule));
    card.append(img, bodyEl);
    return card;
  }

  close(): void {
    this.modal?.close();
    this.modal = null;
  }
}
